import { useSyncExternalStore } from 'react';
import { PALETTE, type PaletteItem } from './palette';

/** Data types of the builder's drags: the drop targets ignore other drags (files, text, links). */
export const PALETTE_MIME = 'application/x-evidencia-palette';
export const FIELD_MIME = 'application/x-evidencia-field';

/** A control dragged from the palette, or a field of the list being moved; `id` tells one drag from the next. */
export type BuilderDrag = { id: number; kind: 'palette'; item: PaletteItem } | { id: number; kind: 'field'; key: string };

let current: BuilderDrag | null = null;
let counter = 0;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function start(e: React.DragEvent, type: string, value: string, effect: 'copy' | 'move', drag: BuilderDrag) {
  e.dataTransfer.clearData();
  e.dataTransfer.setData(type, value);
  e.dataTransfer.effectAllowed = effect;
  current = drag;
  // re-render once the browser has taken the drag image (changing the DOM in dragstart can cancel the drag)
  setTimeout(notify, 0);
}

export function startPaletteDrag(e: React.DragEvent, item: PaletteItem): void {
  start(e, PALETTE_MIME, item.id, 'copy', { id: ++counter, kind: 'palette', item });
}

/** Moving the field `key` by its grip; the drag image is the field's row. */
export function startFieldDrag(e: React.DragEvent, key: string, row: Element | null): void {
  start(e, FIELD_MIME, key, 'move', { id: ++counter, kind: 'field', key });
  if (row) e.dataTransfer.setDragImage(row, 16, 16);
}

/** On dragend of the source, and on a drop (the source may be gone by the time dragend would fire). */
export function endBuilderDrag(): void {
  if (!current) return;
  current = null;
  notify();
}

/** The current drag (null when none). */
export function useBuilderDrag(): BuilderDrag | null {
  return useSyncExternalStore(subscribe, () => current, () => null);
}

/** The builder drag of a drag event; null for other drags. */
export function builderDragOf(e: React.DragEvent): BuilderDrag | null {
  const types = Array.from(e.dataTransfer?.types ?? []);
  if (types.includes(FIELD_MIME)) return current?.kind === 'field' ? current : null;
  if (!types.includes(PALETTE_MIME)) return null;
  if (current) return current;
  // dragged from another window: the data can only be read on drop
  const item = PALETTE.find((i) => i.id === e.dataTransfer.getData(PALETTE_MIME));
  return item ? { id: 0, kind: 'palette', item } : null;
}
